import {useMemo} from 'react';

import {isPanelGrid} from '../../components/Slate/plugins/panel-grid';
import {useMemoizedSelector, useSelector} from '../../state/hooks';
import * as ReportViewsSelectors from '../../state/reports/selectors';
import {type ReportViewRef} from '../../state/reports/types';

export interface ProjectSpecifier {
  entityName: string;
  projectName: string;
}

export function useReportProjects(viewRef: ReportViewRef): ProjectSpecifier[] {
  const view = useSelector(state => state.views.views[viewRef.id]);
  const reportNorm = useMemoizedSelector(
    ReportViewsSelectors.getReportPart,
    viewRef
  );
  const panelGrids = reportNorm.blocks.filter(isPanelGrid);
  const runSets = panelGrids.flatMap(pg => pg.metadata.runSets || []);

  const pss: Array<{entityName: string; projectName: string}> = [];
  if (view.project != null) {
    pss.push({
      entityName: view.project.entityName,
      projectName: view.project.name,
    });
  }
  for (const runSet of runSets) {
    if (runSet.project != null) {
      const ps = {
        entityName: runSet.project.entityName,
        projectName: runSet.project.name,
      };
      pss.push(ps);
    }
  }
  const pssKey = getProjectSpecifierCollectionKey(pss);
  return useMemo(() => {
    const projectSpecifierMap = getProjectSpecifierMap(pss);
    return Object.values(projectSpecifierMap);
    // eslint-disable-next-line
  }, [pssKey]);
}

interface ProjectSpecifierMap {
  [key: string]: ProjectSpecifier;
}

export function getProjectSpecifierMap(
  pss: ProjectSpecifier[]
): ProjectSpecifierMap {
  const map: ProjectSpecifierMap = {};
  for (const ps of pss) {
    const key = getProjectSpecifierKey(ps);
    map[key] = ps;
  }
  return map;
}

export function getProjectSpecifierKey(ps: ProjectSpecifier): string {
  return `${ps.entityName},${ps.projectName}`;
}

function getProjectSpecifierCollectionKey(pss: ProjectSpecifier[]): string {
  return pss.map(getProjectSpecifierKey).join(':');
}
